import { useState, useRef, MouseEventHandler, useEffect, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'app/hooks';
import { useGetGroupsQuery } from 'app/api/fleet';
import { useAppSelector } from 'app/store/store';
import { VesselGroupValue } from '../VesselGroupValue';
import { Icon } from 'app/components';

import { GroupInfo } from 'ais-facade-api/api';
import styles from './GroupSelect.module.scss';

interface SelectProps {
  onDeleteGroup?: () => void;
  onChange?: (groupKey: string) => void;
}

interface OptionProps {
  option: GroupInfo;
  onClick: (value: GroupInfo) => void;
  children?: ReactNode;
}

const Option = ({ option, onClick, children }: OptionProps) => {
  const optionRef = useRef<HTMLLIElement>(null);

  const handleClick =
    (clickedValue: GroupInfo): MouseEventHandler<HTMLLIElement> =>
      () => {
        onClick(clickedValue);
      };

  if (option.key) {
    return (
      <li className={styles.option} ref={optionRef} value={option.key} onClick={handleClick(option)} tabIndex={0}>
        {option.color ? <div className={styles.square_color} style={{ backgroundColor: option.color }} /> : children}
        <span className={styles.label} title={option?.name && option?.name?.length >= 18 ? option?.name : undefined}>
          {option.name}
        </span>
      </li>
    );
  }
  return null;
};

export const GroupSelect = ({ onChange, onDeleteGroup }: SelectProps) => {
  const { t } = useTranslation();

  const { data } = useGetGroupsQuery();

  const selectedGroupsFromTable: Array<GroupInfo> = useAppSelector((state) => state.tableSlice.fromTable.groupArray);
  const selectedVesselsFromTable: Array<number> = useAppSelector((state) => state.tableSlice.fromTable.mmsiArray);

  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupInfo | null>(null);

  useOnClickOutside(rootRef, () => {
    setIsOpen(false);
  });

  const handleOptionClick = (value: GroupInfo) => {
    if (value?.name && value?.key) {
      setSelectedGroup(null);

      onChange?.(value.key);
    }
  };

  const handleNullOptionClick = (value: GroupInfo) => {
    if (value?.name && value?.key) {
      setSelectedGroup(null);
      onDeleteGroup?.();
    }
  };

  const handleClick = () => {
    setIsOpen((prev) => !prev);
  };

  const isSeveralGroupsSelected = useMemo(() => {
    return (
      selectedGroupsFromTable?.length >= 1 &&
      (selectedGroupsFromTable.some((group) => group?.key !== selectedGroupsFromTable[0].key) ||
        selectedVesselsFromTable?.length > selectedGroupsFromTable?.length)
    );
  }, [selectedGroupsFromTable, selectedVesselsFromTable]);

  const isOneGroupSelected = useMemo(() => {
    return (
      selectedVesselsFromTable?.length === 1 ||
      (selectedVesselsFromTable.length === selectedGroupsFromTable.length &&
        selectedGroupsFromTable.every((group) => group?.key === selectedGroupsFromTable[0].key))
    );
  }, [selectedGroupsFromTable, selectedVesselsFromTable]);

  useEffect(() => {
    if (isOneGroupSelected) {
      setSelectedGroup(selectedGroupsFromTable[0]);
    }
  }, [isOneGroupSelected, selectedGroupsFromTable]);

  const disabled = useMemo(() => !selectedVesselsFromTable || selectedVesselsFromTable?.length === 0, [selectedVesselsFromTable]);

  const placeholderRef = useRef<HTMLDivElement>(null);

  return (
    <div
      tabIndex={0}
      ref={rootRef}
      className={styles.wrapper}
      data-is-active={isOpen}
      data-aria-disabled={disabled}
      onClick={handleClick}
    >
      <div className={styles.placeholder} ref={placeholderRef}>

        {isSeveralGroupsSelected ? (
          <>
            <div className={styles.square_color}></div>
            <span className={styles.group_label}>{t('SeveralGroup')}</span>
          </>
        ) : (
          <VesselGroupValue group={selectedGroup} />
        )}


        <Icon name="arrow-down" size="16px" />
      </div>

      {isOpen && (
        <ul className={styles.select}>
          <Option key={'option-group-delete'} option={{ key: 'nogroup', name: t('NoGroup') }} onClick={handleNullOptionClick} >
            <Icon name="minus-20" size="20px" />
          </Option>
          {data?.results?.map((option, i) => (
            <Option key={i} option={option} onClick={handleOptionClick} />
          ))}
        </ul>
      )}
    </div>
  );
};
